import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  api,
} from "../api";

function clean(value) {
  return String(value ?? "").trim();
}

function safeObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  )
    ? value
    : {};
}

function initialPlan(project, projectPlan) {
  const saved =
    safeObject(
      projectPlan?.publicationPlan
    );

  const front =
    safeObject(saved.frontCover);

  const back =
    safeObject(saved.backCover);

  const print =
    safeObject(saved.printSettings);

  return {
    frontCover: {
      approach:
        clean(front.approach),
      title:
        clean(front.title) ||
        clean(project?.title),
      subtitle:
        clean(front.subtitle) ||
        clean(
          projectPlan?.overview?.subtitle
        ),
      authorName:
        clean(front.authorName),
    },
    backCover: {
      approach:
        clean(back.approach),
      blurb:
        clean(back.blurb),
      authorBio:
        clean(back.authorBio),
      isbn:
        clean(back.isbn),
    },
    printSettings: {
      paperType:
        clean(print.paperType),
      interiorColor:
        clean(print.interiorColor),
      binding:
        clean(print.binding),
    },
  };
}

function activeAsset(assets, purpose) {
  return (
    (Array.isArray(assets) ? assets : [])
      .find(
        (asset) =>
          clean(asset?.purpose) ===
            purpose &&
          clean(asset?.status) ===
            "active"
      ) ||
    null
  );
}

function CoverUpload({
  isFR,
  projectId,
  purpose,
  label,
  asset,
  enabled,
  onUploaded,
}) {
  const [file, setFile] =
    useState(null);

  const [
    creatorOrSource,
    setCreatorOrSource,
  ] = useState(
    clean(
      asset?.creatorOrSource
    )
  );

  const [
    rightsOrLicense,
    setRightsOrLicense,
  ] = useState(
    clean(
      asset?.rightsOrLicense
    )
  );

  const [
    rightsConfirmed,
    setRightsConfirmed,
  ] = useState(
    asset?.rightsConfirmed ===
      true
  );

  const [
    uploading,
    setUploading,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [
    previewUrl,
    setPreviewUrl,
  ] = useState("");

  useEffect(() => {
    setCreatorOrSource(
      clean(
        asset?.creatorOrSource
      )
    );

    setRightsOrLicense(
      clean(
        asset?.rightsOrLicense
      )
    );

    setRightsConfirmed(
      asset?.rightsConfirmed ===
        true
    );
  }, [asset]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    const publicUrl =
      clean(
        asset?.publicUrl
      );

    setPreviewUrl("");

    if (!publicUrl) {
      return () => {};
    }

    async function load() {
      try {
        const blob =
          await api
            .getTextbookPublicationAssetFile(
              publicUrl
            );

        if (
          cancelled ||
          typeof URL ===
            "undefined" ||
          typeof URL.createObjectURL !==
            "function"
        ) {
          return;
        }

        objectUrl =
          URL.createObjectURL(
            blob
          );

        if (!cancelled) {
          setPreviewUrl(
            objectUrl
          );
        }
      } catch {
        // Metadata remains authoritative even when preview loading fails.
      }
    }

    load();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !==
          "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [asset?.publicUrl]);

  async function upload() {
    if (!file) {
      setError(
        isFR
          ? "Choisissez une image de couverture."
          : "Choose a cover image."
      );
      return;
    }

    if (
      !clean(creatorOrSource) ||
      !clean(rightsOrLicense) ||
      !rightsConfirmed
    ) {
      setError(
        isFR
          ? "Indiquez la source, les droits et confirmez votre autorisation."
          : "Provide the source, rights or licence, and confirm you have permission to use the image."
      );
      return;
    }

    try {
      setUploading(true);
      setError("");

      const payload =
        await api
          .uploadTextbookPublicationCover(
            projectId,
            purpose,
            file,
            {
              creatorOrSource,
              rightsOrLicense,
              rightsConfirmed,
              expectedRevision:
                Number(
                  asset?.revision ||
                  0
                ),
            }
          );

      setFile(null);

      if (
        typeof onUploaded ===
          "function"
      ) {
        onUploaded(
          payload
            ?.publicationAsset ||
          null
        );
      }
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de téléverser la couverture."
            : "Could not upload the cover."
        )
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 16,
        paddingTop: 14,
        borderTop:
          "1px solid #e2e8f0",
      }}
    >
      <strong>{label}</strong>

      {asset ? (
        <div
          style={{
            marginTop: 10,
            display: "grid",
            gap: 8,
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={label}
              style={{
                width: "100%",
                maxHeight: 260,
                objectFit:
                  "contain",
                borderRadius: 10,
                border:
                  "1px solid #dbe5df",
                background:
                  "#f8fafc",
              }}
            />
          ) : null}

          <div className="ns-card-meta">
            <strong>
              {isFR
                ? "Image enregistrée"
                : "Saved image"}:
            </strong>
            {" "}
            {clean(asset.fileName) ||
              (isFR
                ? "Couverture"
                : "Cover")}
          </div>

          {Number(
            asset
              ?.verifiedImageMetadata
              ?.widthPixels ||
            0
          ) > 0 &&
          Number(
            asset
              ?.verifiedImageMetadata
              ?.heightPixels ||
            0
          ) > 0 ? (
            <div className="ns-card-meta">
              {
                asset
                  .verifiedImageMetadata
                  .widthPixels
              }
              {" × "}
              {
                asset
                  .verifiedImageMetadata
                  .heightPixels
              }
              {" px"}
            </div>
          ) : null}
        </div>
      ) : (
        <p
          className="ns-card-meta"
          style={{
            fontSize: 12,
          }}
        >
          {isFR
            ? "Aucune image de couverture enregistrée."
            : "No saved cover image yet."}
        </p>
      )}

      <label
        style={{
          display: "block",
          marginTop: 10,
        }}
      >
        <strong>
          {asset
            ? (isFR
                ? "Remplacer l’image"
                : "Replace image")
            : (isFR
                ? "Téléverser l’image"
                : "Upload image")}
        </strong>

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={
            !enabled ||
            uploading
          }
          onChange={(event) =>
            setFile(
              event.target
                .files?.[0] ||
              null
            )
          }
          style={{
            width: "100%",
            maxWidth: "none",
            marginTop: 6,
          }}
        />
      </label>

      <label
        style={{
          display: "block",
          marginTop: 10,
        }}
      >
        <strong>
          {isFR
            ? "Créateur / source"
            : "Creator / source"}
        </strong>

        <input
              className="ns-input"
          value={creatorOrSource}
          disabled={
            !enabled ||
            uploading
          }
          onChange={(event) =>
            setCreatorOrSource(
              event.target.value
            )
          }
          style={{
            width: "100%",
            maxWidth: "none",
            marginTop: 6,
          }}
        />
      </label>

      <label
        style={{
          display: "block",
          marginTop: 10,
        }}
      >
        <strong>
          {isFR
            ? "Droits / licence"
            : "Rights / licence"}
        </strong>

        <input
              className="ns-input"
          value={rightsOrLicense}
          disabled={
            !enabled ||
            uploading
          }
          onChange={(event) =>
            setRightsOrLicense(
              event.target.value
            )
          }
          placeholder={
            isFR
              ? "Ex. œuvre de l’auteur, licence, autorisation"
              : "e.g. author-owned, licence, permission"
          }
          style={{
            width: "100%",
            maxWidth: "none",
            marginTop: 6,
          }}
        />
      </label>

      <label
        style={{
          display: "flex",
          gap: 8,
          alignItems:
            "flex-start",
          marginTop: 10,
          fontSize: 12,
        }}
      >
        <input
          type="checkbox"
          checked={rightsConfirmed}
          disabled={
            !enabled ||
            uploading
          }
          onChange={(event) =>
            setRightsConfirmed(
              event.target.checked
            )
          }
        />

        <span>
          {isFR
            ? "Je confirme avoir le droit d’utiliser cette image pour cette publication."
            : "I confirm that I have the right to use this image for this publication."}
        </span>
      </label>

      {error ? (
        <div
          style={{
            marginTop: 8,
            color: "#b91c1c",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      <button
        type="button"
        className="ns-button"
        disabled={
          !enabled ||
          uploading ||
          !file
        }
        onClick={upload}
        style={{
          marginTop: 10,
        }}
      >
        {uploading
          ? (isFR
              ? "Téléversement…"
              : "Uploading…")
          : asset
            ? (isFR
                ? "Remplacer la couverture"
                : "Replace Cover")
            : (isFR
                ? "Téléverser la couverture"
                : "Upload Cover")}
      </button>
    </div>
  );
}

export default function TextbookPublicationPreparation({
  project,
  projectPlan,
  onSave,
  onOpenReview,
  canEdit = false,
  isFR = false,
}) {
  const [draft, setDraft] =
    useState(() =>
      initialPlan(
        project,
        projectPlan
      )
    );

  const [
    publicationAssets,
    setPublicationAssets,
  ] = useState([]);

  const [
    assetsLoading,
    setAssetsLoading,
  ] = useState(true);

  const [saving, setSaving] =
    useState(false);

  const [
    generatingInterior,
    setGeneratingInterior,
  ] = useState(false);

  const [
    approvingInterior,
    setApprovingInterior,
  ] = useState(false);

  const [
    interiorReview,
    setInteriorReview,
  ] = useState(null);

  const [
    interiorActionMessage,
    setInteriorActionMessage,
  ] = useState("");

  const [
    interiorActionError,
    setInteriorActionError,
  ] = useState("");

  const [
    interiorMismatchDetails,
    setInteriorMismatchDetails,
  ] = useState(null);

  const [
    coverGeometry,
    setCoverGeometry,
  ] = useState(null);

  const [
    loadingCoverGeometry,
    setLoadingCoverGeometry,
  ] = useState(false);

  const [
    coverGeometryError,
    setCoverGeometryError,
  ] = useState("");

  const [
    generatingCoverReview,
    setGeneratingCoverReview,
  ] = useState(false);

  const [
    coverReviewMessage,
    setCoverReviewMessage,
  ] = useState("");

  const [
    coverReviewError,
    setCoverReviewError,
  ] = useState("");

  const [
    coverReviewBlockDetails,
    setCoverReviewBlockDetails,
  ] = useState(null);

  const [
    coverReviewIdentity,
    setCoverReviewIdentity,
  ] = useState(null);

  const [
    approvingCover,
    setApprovingCover,
  ] = useState(false);

  const [
    coverApproved,
    setCoverApproved,
  ] = useState(false);

  const [
    coverApprovalMessage,
    setCoverApprovalMessage,
  ] = useState("");

  const [
    coverApprovalError,
    setCoverApprovalError,
  ] = useState("");

  const [
    persistingPublicationArtifacts,
    setPersistingPublicationArtifacts,
  ] = useState(false);

  const [
    publicationArtifactsPersisted,
    setPublicationArtifactsPersisted,
  ] = useState(false);

  const [
    publicationArtifactMessage,
    setPublicationArtifactMessage,
  ] = useState("");

  const [
    publicationArtifactError,
    setPublicationArtifactError,
  ] = useState("");

  const [
    buildingFinalPublicationPackage,
    setBuildingFinalPublicationPackage,
  ] = useState(false);

  const [
    finalPublicationPackageReady,
    setFinalPublicationPackageReady,
  ] = useState(false);

  const [
    finalPublicationPackageMessage,
    setFinalPublicationPackageMessage,
  ] = useState("");

  const [
    finalPublicationPackageError,
    setFinalPublicationPackageError,
  ] = useState("");

  const [
    publicationFileLoadingRole,
    setPublicationFileLoadingRole,
  ] = useState("");

  const [
    publicationFileError,
    setPublicationFileError,
  ] = useState("");

  const [
    coverRebindRequired,
    setCoverRebindRequired,
  ] = useState(false);

  const [
    rebindingCoverArtwork,
    setRebindingCoverArtwork,
  ] = useState(false);

  const [
    coverRebindMessage,
    setCoverRebindMessage,
  ] = useState("");

  const [
    coverRebindError,
    setCoverRebindError,
  ] = useState("");

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  useEffect(() => {
    setDraft(
      initialPlan(
        project,
        projectPlan
      )
    );
  }, [
    project,
    projectPlan,
  ]);

  useEffect(() => {
    let cancelled = false;

    const projectId =
      clean(project?.id);

    if (!projectId) {
      setPublicationAssets([]);
      setAssetsLoading(false);
      return () => {};
    }

    setAssetsLoading(true);

    async function loadAssets() {
      try {
        const payload =
          await api
            .getTextbookPublicationAssets(
              projectId
            );

        if (!cancelled) {
          setPublicationAssets(
            Array.isArray(
              payload
                ?.publicationAssets
            )
              ? payload
                  .publicationAssets
              : []
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            clean(err?.message) ||
            (
              isFR
                ? "Impossible de charger les couvertures enregistrées."
                : "Could not load saved cover assets."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      }
    }

    loadAssets();

    return () => {
      cancelled = true;
    };
  }, [
    isFR,
    project?.id,
  ]);

  useEffect(() => {
    let cancelled = false;

    const projectId =
      clean(project?.id);

    if (!projectId) {
      return () => {};
    }

    async function restoreApprovedInterior() {
      try {
        const result =
          await api
            .getTextbookCurrentProductionRun(
              projectId
            );

        if (
          cancelled ||
          result?.ok !== true ||
          result?.current !== true ||
          result?.interiorApproved !== true ||
          result?.finalPageCountLocked !== true
        ) {
          return;
        }

        const productionRunId =
          clean(
            result
              ?.productionRunId
          );

        const finalPageCount =
          Number(
            result
              ?.finalPageCount ||
            0
          );

        if (
          !productionRunId ||
          !Number.isInteger(
            finalPageCount
          ) ||
          finalPageCount <= 0
        ) {
          return;
        }

        setInteriorReview(
          (current) => {
            /*
             * Never overwrite a review generated in this live session.
             */
            if (current) {
              return current;
            }

            return {
              productionRunId,

              pageCount:
                finalPageCount,

              approvedFinalPageCount:
                finalPageCount,

              creatorApprovalStillRequired:
                false,

              finalPageCountLocked:
                true,

              approvalReady:
                false,

              restoredFromApprovedRun:
                true,
            };
          }
        );

        setInteriorActionMessage(
          isFR
            ? `Intérieur déjà approuvé · ${finalPageCount} pages verrouillées.`
            : `Approved Interior restored · ${finalPageCount} pages locked.`
        );

        setCoverRebindRequired(
          result
            ?.coverRebindRequired ===
              true
        );

        const restoredCoverApproved =
          result
            ?.coverApprovalLocked ===
              true &&
          result
            ?.coverApproved ===
              true;

        setCoverApproved(
          restoredCoverApproved
        );

        setCoverApprovalMessage(
          restoredCoverApproved
            ? (
                isFR
                  ? "Couverture finale déjà approuvée · identité exacte verrouillée."
                  : "Final Cover already approved · exact reviewed identity locked."
              )
            : ""
        );

        setCoverApprovalError(
          ""
        );

        const restoredPublicationArtifacts =
          restoredCoverApproved &&
          result
            ?.publicationArtifactsPersisted ===
              true &&
          result
            ?.artifactPersistenceLocked ===
              true;

        setPublicationArtifactsPersisted(
          restoredPublicationArtifacts
        );

        setPublicationArtifactMessage(
          restoredPublicationArtifacts
            ? (
                isFR
                  ? "Artefacts de publication approuvés déjà persistés · octets exacts de l’intérieur et de la couverture revérifiés."
                  : "Approved publication artifacts already persisted · exact Interior and Final Cover bytes reverified."
              )
            : ""
        );

        setPublicationArtifactError(
          ""
        );

        const restoredFinalPublicationPackage =
          restoredPublicationArtifacts &&
          result?.finalPublicationPackageBuilt === true &&
          result?.publicationPackageComplete === true &&
          result?.publicationReady === true &&
          result?.publicationSubmitted !== true &&
          result?.publicationUploadPerformed !== true;

        setFinalPublicationPackageReady(
          restoredFinalPublicationPackage
        );

        setFinalPublicationPackageMessage(
          restoredFinalPublicationPackage
            ? (
                isFR
                  ? "Paquet final de publication déjà prêt · octets persistés exacts revérifiés. Aucune soumission ni aucun téléversement n’a été effectué."
                  : "Final Publication Package already ready · exact persisted bytes reverified. No publication submission or upload has occurred."
              )
            : ""
        );

        setFinalPublicationPackageError(
          ""
        );

        setCoverRebindMessage(
          result
            ?.coverArtworkRebound ===
              true
            ? (
                isFR
                  ? "Les couvertures actuelles sont liées à l’intérieur approuvé."
                  : "Current cover artwork is bound to the approved Interior."
              )
            : ""
        );

        /*
         * V1B.1 continuity:
         * the approved run already owns the locked page count and
         * print profile, so restore its server-derived cover geometry
         * immediately instead of making the creator recalculate it
         * after every page refresh.
         */
        try {
          if (!cancelled) {
            setLoadingCoverGeometry(
              true
            );

            setCoverGeometryError(
              ""
            );
          }

          const geometryResult =
            await api
              .getTextbookFinalCoverGeometry(
                projectId,
                productionRunId
              );

          if (cancelled) {
            return;
          }

          if (
            geometryResult?.ok !== true ||
            geometryResult
              ?.finalPageCountLocked !==
                true ||
            geometryResult
              ?.coverGeometryReady !==
                true
          ) {
            throw new Error(
              isFR
                ? "L’intérieur approuvé a été restauré, mais sa géométrie finale n’a pas pu être rétablie."
                : "The approved Interior was restored, but its Final Cover geometry could not be restored."
            );
          }

          setCoverGeometry(
            geometryResult
          );
        } catch (
          geometryError
        ) {
          if (!cancelled) {
            /*
             * Keep the approved Interior restored even if geometry
             * continuity is temporarily unavailable. The manual
             * Calculate Final Cover Geometry action remains available.
             */
            setCoverGeometryError(
              clean(
                geometryError
                  ?.message
              ) ||
              (
                isFR
                  ? "Impossible de restaurer automatiquement la géométrie finale de la couverture."
                  : "Could not automatically restore the Final Cover geometry."
              )
            );
          }
        } finally {
          if (!cancelled) {
            setLoadingCoverGeometry(
              false
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          /*
           * Do not block Publication Preparation if continuity lookup
           * is temporarily unavailable. The normal Interior workflow
           * remains available.
           */
          setCoverGeometryError(
            clean(err?.message) ||
            (
              isFR
                ? "Impossible de restaurer l’état d’approbation de l’intérieur."
                : "Could not restore the approved Interior state."
            )
          );
        }
      }
    }

    restoreApprovedInterior();

    return () => {
      cancelled = true;
    };
  }, [
    isFR,
    project?.id,
  ]);

  const frontAsset =
    activeAsset(
      publicationAssets,
      "front_cover"
    );

  const backAsset =
    activeAsset(
      publicationAssets,
      "back_cover"
    );

  const effectiveFrontApproach =
    clean(
      draft
        .frontCover
        .approach
    ) ||
    (
      frontAsset
        ? "existing_cover"
        : ""
    );

  const effectiveBackApproach =
    clean(
      draft
        .backCover
        .approach
    ) ||
    (
      backAsset
        ? "existing_cover"
        : ""
    );

  useEffect(() => {
    if (
      frontAsset &&
      !clean(
        draft
          .frontCover
          .approach
      )
    ) {
      setDraft(
        (current) => ({
          ...current,
          frontCover: {
            ...current.frontCover,
            approach:
              "existing_cover",
          },
        })
      );
    }

    if (
      backAsset &&
      !clean(
        draft
          .backCover
          .approach
      )
    ) {
      setDraft(
        (current) => ({
          ...current,
          backCover: {
            ...current.backCover,
            approach:
              "existing_cover",
          },
        })
      );
    }
  }, [
    backAsset,
    draft.backCover.approach,
    draft.frontCover.approach,
    frontAsset,
  ]);

  const readiness =
    useMemo(() => {
      const items =
        Array.isArray(
          projectPlan
            ?.bookMap
            ?.items
        )
          ? projectPlan
              .bookMap
              .items
          : [];

      const chapters =
        items.filter(
          (item) =>
            clean(item?.type) ===
              "chapter"
        ).length;

      const frontMatter =
        items.filter(
          (item) =>
            clean(item?.type) ===
              "front_matter"
        ).length;

      const backMatter =
        items.filter(
          (item) =>
            clean(item?.type) ===
              "back_matter"
        ).length;

      const frontApproach =
        effectiveFrontApproach;

      const backApproach =
        effectiveBackApproach;

      const frontReady =
        frontApproach ===
          "nextscenes_layout"
          ? Boolean(
              clean(
                draft
                  .frontCover
                  .title
              )
            )
          : (
              frontApproach ===
                "existing_cover"
                ? Boolean(
                    frontAsset &&
                    frontAsset
                      .rightsConfirmed ===
                        true
                  )
                : false
            );

      const backReady =
        backApproach ===
          "nextscenes_layout"
          ? true
          : (
              backApproach ===
                "existing_cover"
                ? Boolean(
                    backAsset &&
                    backAsset
                      .rightsConfirmed ===
                        true
                  )
                : false
            );

      const publicationAuthority =
        safeObject(
          projectPlan
            ?.publicationManuscriptAuthority
        );

      const publicationManuscriptLocked =
        clean(
          publicationAuthority
            ?.status
        ) === "locked" &&
        Boolean(
          clean(
            publicationAuthority
              ?.snapshotId
          )
        );

      const checks = [
        {
          label:
            isFR
              ? "Format du livre"
              : "Book format",
          ready:
            Boolean(
              clean(
                projectPlan
                  ?.formatPlan
                  ?.trimSize
              ) &&
              clean(
                projectPlan
                  ?.formatPlan
                  ?.publicationFormat
              )
            ),
        },
        {
          label:
            isFR
              ? "Couverture avant"
              : "Front Cover",
          ready:
            frontReady,
        },
        {
          label:
            isFR
              ? "Manuscrit"
              : "Interior manuscript",
          ready:
            chapters > 0,
        },
        {
          label:
            isFR
              ? "Manuscrit de publication"
              : "Publication manuscript",
          ready:
            publicationManuscriptLocked,
        },
        {
          label:
            isFR
              ? "Couverture arrière"
              : "Back Cover",
          ready:
            backReady,
        },
        {
          label:
            isFR
              ? "Réglages d’impression"
              : "Print Settings",
          ready:
            Boolean(
              clean(
                draft
                  .printSettings
                  .paperType
              ) &&
              clean(
                draft
                  .printSettings
                  .interiorColor
              ) &&
              clean(
                draft
                  .printSettings
                  .binding
              )
            ),
        },
      ];

      return {
        checks,
        publicationManuscriptLocked,
        readyCount:
          checks.filter(
            (check) =>
              check.ready
          ).length,
        frontMatter,
        chapters,
        backMatter,
      };
    }, [
      backAsset,
      draft,
      effectiveBackApproach,
      effectiveFrontApproach,
      frontAsset,
      isFR,
      projectPlan,
    ]);

  async function loadFinalCoverGeometry(
    productionRunId
  ) {
    const projectId =
      clean(project?.id);

    const runId =
      clean(productionRunId);

    if (!projectId || !runId) {
      setCoverGeometryError(
        isFR
          ? "La géométrie finale exige un intérieur approuvé."
          : "Final Cover geometry requires an approved Interior."
      );

      return null;
    }

    try {
      setLoadingCoverGeometry(
        true
      );

      setCoverGeometryError("");

      const result =
        await api
          .getTextbookFinalCoverGeometry(
            projectId,
            runId
          );

      if (
        result?.ok !== true ||
        result
          ?.finalPageCountLocked !==
            true ||
        result
          ?.coverGeometryReady !==
            true
      ) {
        throw new Error(
          isFR
            ? "La géométrie physique de la couverture n’a pas pu être établie."
            : "The physical Final Cover geometry could not be established."
        );
      }

      setCoverGeometry(
        result
      );

      return result;
    } catch (err) {
      setCoverGeometryError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de calculer la géométrie finale de la couverture."
            : "Could not calculate the Final Cover geometry."
        )
      );

      return null;
    } finally {
      setLoadingCoverGeometry(
        false
      );
    }
  }


  async function rebindCurrentCoverArtwork() {
    const projectId =
      clean(project?.id);

    const productionRunId =
      clean(
        interiorReview
          ?.productionRunId
      );

    if (
      !projectId ||
      !productionRunId ||
      interiorReview
        ?.finalPageCountLocked !==
          true
    ) {
      setCoverRebindError(
        isFR
          ? "La liaison des couvertures exige l’intérieur approuvé et verrouillé."
          : "Cover rebinding requires the approved locked Interior."
      );

      return;
    }

    try {
      setRebindingCoverArtwork(
        true
      );

      setCoverRebindError(
        ""
      );

      setCoverRebindMessage(
        isFR
          ? "Vérification et liaison des couvertures actuelles…"
          : "Verifying and binding the current cover artwork…"
      );

      const result =
        await api
          .rebindTextbookCoverArtwork(
            projectId,
            productionRunId
          );

      if (
        result?.ok !== true ||
        result
          ?.coverArtworkRebound !==
            true ||
        result
          ?.finalPageCountLocked !==
            true
      ) {
        throw new Error(
          isFR
            ? "Les couvertures actuelles n’ont pas pu être liées en toute sécurité."
            : "The current cover artwork could not be rebound safely."
        );
      }

      setCoverRebindRequired(
        false
      );

      setCoverRebindMessage(
        isFR
          ? `Couvertures liées · intérieur approuvé de ${Number(
              result?.finalPageCount || 0
            )} pages inchangé.`
          : `Cover artwork bound · approved ${Number(
              result?.finalPageCount || 0
            )}-page Interior unchanged.`
      );

      setCoverReviewError(
        ""
      );

      setCoverReviewBlockDetails(
        null
      );

      setCoverReviewIdentity(
        null
      );

      setCoverApprovalMessage(
        ""
      );

      setCoverApprovalError(
        ""
      );
    } catch (err) {
      setCoverRebindRequired(
        true
      );

      setCoverRebindMessage(
        ""
      );

      setCoverRebindError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de lier les couvertures actuelles."
            : "Could not bind the current cover artwork."
        )
      );
    } finally {
      setRebindingCoverArtwork(
        false
      );
    }
  }


  async function generateCoverReview() {
    const projectId =
      clean(project?.id);

    const productionRunId =
      clean(
        interiorReview
          ?.productionRunId
      );

    if (
      !projectId ||
      !productionRunId ||
      coverGeometry
        ?.coverGeometryReady !==
          true
    ) {
      setCoverReviewError(
        isFR
          ? "Le PDF de vérification de couverture exige d’abord une géométrie physique verrouillée."
          : "Cover Review requires locked physical cover geometry first."
      );

      return;
    }

    let reviewWindow =
      null;

    if (
      typeof window !==
        "undefined" &&
      typeof window.open ===
        "function"
    ) {
      reviewWindow =
        window.open(
          "",
          "_blank"
        );
    }

    try {
      setGeneratingCoverReview(
        true
      );

      setCoverReviewError("");
      setCoverReviewMessage(
        isFR
          ? "Préparation du PDF de vérification de couverture…"
          : "Preparing the Cover Review PDF…"
      );
      setCoverReviewBlockDetails(
        null
      );

      const result =
        await api
          .generateTextbookCoverReview(
            projectId,
            productionRunId
          );

      if (
        result
          ?.coverReviewReady !==
            true ||
        !result?.blob
      ) {
        throw new Error(
          isFR
            ? "Le PDF de vérification de couverture n’a pas pu être établi."
            : "The Cover Review PDF could not be established."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          result.blob
        );

      if (
        reviewWindow &&
        !reviewWindow.closed
      ) {
        reviewWindow.location =
          objectUrl;
      } else {
        window.open(
          objectUrl,
          "_blank"
        );
      }

      window.setTimeout(
        () => {
          URL.revokeObjectURL(
            objectUrl
          );
        },
        60_000
      );

      setCoverReviewIdentity({
        finalPageCount:
          Number(
            result
              ?.finalPageCount ||
            0
          ),

        contentSha256:
          clean(
            result
              ?.contentSha256
          ),

        reviewCandidateFingerprint:
          clean(
            result
              ?.reviewCandidateFingerprint
          ),
      });

      setCoverApprovalError(
        ""
      );

      setCoverReviewMessage(
        isFR
          ? `PDF de vérification de couverture prêt · ${result.finalPageCount} pages verrouillées. Vérifiez l’enveloppe complète avant toute approbation.`
          : `Cover Review PDF ready · ${result.finalPageCount} locked pages. Inspect the full wrap before any Cover Approval.`
      );
    } catch (err) {
      if (
        reviewWindow &&
        !reviewWindow.closed
      ) {
        reviewWindow.close();
      }

      setCoverReviewBlockDetails(
        safeObject(
          err
            ?.data
            ?.details
        )
      );

      const actionError =
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de générer le PDF de vérification de couverture."
            : "Could not generate the Cover Review PDF."
        );

      setCoverReviewError(
        actionError
      );

      setCoverReviewMessage("");
    } finally {
      setGeneratingCoverReview(
        false
      );
    }
  }


  async function approveCover() {
    const projectId =
      clean(project?.id);

    const productionRunId =
      clean(
        interiorReview
          ?.productionRunId
      );

    const review =
      safeObject(
        coverReviewIdentity
      );

    if (
      !projectId ||
      !productionRunId ||
      coverApproved ||
      !Number.isInteger(
        Number(
          review.finalPageCount
        )
      ) ||
      Number(
        review.finalPageCount
      ) <= 0 ||
      !clean(
        review
          .contentSha256
      ) ||
      !clean(
        review
          .reviewCandidateFingerprint
      )
    ) {
      setCoverApprovalError(
        isFR
          ? "Générez et vérifiez d’abord le PDF exact de la couverture finale."
          : "Generate and inspect the exact Final Cover Review PDF before approval."
      );

      return;
    }

    try {
      setApprovingCover(
        true
      );

      setCoverApprovalError(
        ""
      );

      setCoverApprovalMessage(
        isFR
          ? "Vérification finale et verrouillage de la couverture approuvée…"
          : "Verifying and locking the approved Final Cover…"
      );

      const result =
        await api
          .approveTextbookCover(
            projectId,
            productionRunId,
            {
              finalPageCount:
                review
                  .finalPageCount,

              contentSha256:
                review
                  .contentSha256,

              reviewCandidateFingerprint:
                review
                  .reviewCandidateFingerprint,
            }
          );

      if (
        result?.ok !==
          true ||
        result
          ?.coverApproved !==
            true ||
        result
          ?.coverApprovalLocked !==
            true
      ) {
        throw new Error(
          isFR
            ? "L’approbation exacte de la couverture finale n’a pas pu être verrouillée."
            : "The exact Final Cover Approval could not be locked."
        );
      }

      setCoverApproved(
        true
      );

      setPublicationArtifactsPersisted(
        false
      );

      setFinalPublicationPackageReady(
        false
      );

      setFinalPublicationPackageMessage(
        ""
      );

      setFinalPublicationPackageError(
        ""
      );

      setPublicationArtifactMessage(
        ""
      );

      setPublicationArtifactError(
        ""
      );

      setCoverApprovalMessage(
        isFR
          ? "Couverture finale approuvée · identité exacte verrouillée. La persistance des artefacts approuvés est l’étape suivante."
          : "Final Cover approved · exact reviewed identity locked. Approved publication-artifact persistence is the next production step."
      );

      setCoverApprovalError(
        ""
      );

      setMessage(
        isFR
          ? "Couverture finale approuvée."
          : "Final Cover approved."
      );
    } catch (err) {
      setCoverApprovalMessage(
        ""
      );

      setCoverApprovalError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’approuver la couverture finale."
            : "Could not approve the Final Cover."
        )
      );
    } finally {
      setApprovingCover(
        false
      );
    }
  }




  async function reviewFinalPublicationFile(
    role
  ) {
    const projectId =
      clean(project?.id);

    const productionRunId =
      clean(
        interiorReview
          ?.productionRunId
      );

    const normalizedRole =
      clean(role)
        .toLowerCase();

    if (
      !projectId ||
      !productionRunId ||
      !finalPublicationPackageReady ||
      (
        normalizedRole !==
          "interior" &&
        normalizedRole !==
          "cover"
      )
    ) {
      setPublicationFileError(
        isFR
          ? "Le paquet final doit être prêt avant la consultation des fichiers de publication."
          : "The Final Publication Package must be ready before publication files can be reviewed."
      );

      return;
    }

    let reviewWindow =
      null;

    if (
      typeof window !==
        "undefined" &&
      typeof window.open ===
        "function"
    ) {
      reviewWindow =
        window.open(
          "",
          "_blank"
        );
    }

    try {
      setPublicationFileLoadingRole(
        `review-${normalizedRole}`
      );

      setPublicationFileError(
        ""
      );

      const result =
        await api
          .getTextbookFinalPublicationFile(
            projectId,
            productionRunId,
            normalizedRole
          );

      if (
        !result?.blob
      ) {
        throw new Error(
          isFR
            ? "Le fichier final de publication est indisponible."
            : "The Final publication file is unavailable."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          result.blob
        );

      if (
        reviewWindow &&
        !reviewWindow.closed
      ) {
        reviewWindow.location.href =
          objectUrl;
      } else if (
        typeof window !==
          "undefined"
      ) {
        window.open(
          objectUrl,
          "_blank"
        );
      }

      if (
        typeof window !==
          "undefined" &&
        typeof window.setTimeout ===
          "function" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () =>
            URL.revokeObjectURL(
              objectUrl
            ),
          60000
        );
      }
    } catch (err) {
      if (
        reviewWindow &&
        !reviewWindow.closed
      ) {
        reviewWindow.close();
      }

      setPublicationFileError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’ouvrir le fichier final de publication."
            : "Could not open the Final publication file."
        )
      );
    } finally {
      setPublicationFileLoadingRole(
        ""
      );
    }
  }


  async function downloadFinalPublicationFile(
    role
  ) {
    const projectId =
      clean(project?.id);

    const productionRunId =
      clean(
        interiorReview
          ?.productionRunId
      );

    const normalizedRole =
      clean(role)
        .toLowerCase();

    if (
      !projectId ||
      !productionRunId ||
      !finalPublicationPackageReady ||
      (
        normalizedRole !==
          "interior" &&
        normalizedRole !==
          "cover"
      )
    ) {
      setPublicationFileError(
        isFR
          ? "Le paquet final doit être prêt avant le téléchargement des fichiers de publication."
          : "The Final Publication Package must be ready before publication files can be downloaded."
      );

      return;
    }

    try {
      setPublicationFileLoadingRole(
        `download-${normalizedRole}`
      );

      setPublicationFileError(
        ""
      );

      const result =
        await api
          .getTextbookFinalPublicationFile(
            projectId,
            productionRunId,
            normalizedRole
          );

      if (
        !result?.blob
      ) {
        throw new Error(
          isFR
            ? "Le fichier final de publication est indisponible."
            : "The Final publication file is unavailable."
        );
      }

      const objectUrl =
        URL.createObjectURL(
          result.blob
        );

      const anchor =
        document
          .createElement(
            "a"
          );

      anchor.href =
        objectUrl;

      anchor.download =
        result.fileName;

      document.body
        .appendChild(
          anchor
        );

      anchor.click();

      anchor.remove();

      if (
        typeof window !==
          "undefined" &&
        typeof window.setTimeout ===
          "function" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        window.setTimeout(
          () =>
            URL.revokeObjectURL(
              objectUrl
            ),
          1000
        );
      }
    } catch (err) {
      setPublicationFileError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de télécharger le fichier final de publication."
            : "Could not download the Final publication file."
        )
      );
    } finally {
      setPublicationFileLoadingRole(
        ""
      );
    }
  }


  async function buildFinalPublicationPackage() {
    const projectId = clean(project?.id);
    const productionRunId = clean(interiorReview?.productionRunId);

    if (!projectId || !productionRunId || !publicationArtifactsPersisted || finalPublicationPackageReady) {
      setFinalPublicationPackageError(
        isFR
          ? "Les artefacts de publication approuvés doivent être persistés avant la construction du paquet final."
          : "Approved publication artifacts must be persisted before Final Publication Package construction."
      );
      return;
    }

    try {
      setBuildingFinalPublicationPackage(true);
      setFinalPublicationPackageError("");
      setFinalPublicationPackageMessage(
        isFR
          ? "Revérification des artefacts persistés et construction du paquet final de publication…"
          : "Reverifying persisted artifacts and building the Final Publication Package…"
      );

      const result = await api.buildTextbookFinalPublicationPackage(projectId, productionRunId);
      if (
        result?.ok !== true || result?.finalPublicationPackageBuilt !== true ||
        result?.publicationPackageComplete !== true || result?.exactPublicationArtifactsReverified !== true ||
        result?.publicationReady !== true || result?.publicationSubmitted === true || result?.publicationUploadPerformed === true
      ) {
        throw new Error(
          isFR
            ? "Le paquet final de publication n’a pas pu être établi exactement."
            : "The exact Final Publication Package could not be established."
        );
      }

      setFinalPublicationPackageReady(true);
      setFinalPublicationPackageMessage(
        isFR
          ? "Paquet final de publication prêt · intérieur et couverture persistés revérifiés. Aucune soumission ni aucun téléversement n’a été effectué."
          : "Final Publication Package ready · persisted Interior and Final Cover reverified. No publication submission or upload has occurred."
      );
      setFinalPublicationPackageError("");
      setMessage(isFR ? "Paquet final de publication prêt." : "Final Publication Package ready.");
    } catch (err) {
      setFinalPublicationPackageMessage("");
      setFinalPublicationPackageError(
        clean(err?.message) ||
        (isFR ? "Impossible de construire le paquet final de publication." : "Could not build the Final Publication Package.")
      );
    } finally {
      setBuildingFinalPublicationPackage(false);
    }
  }


  async function persistApprovedPublicationArtifacts() {
    const projectId = clean(project?.id);
    const productionRunId = clean(interiorReview?.productionRunId);

    if (!projectId || !productionRunId || !coverApproved || publicationArtifactsPersisted) {
      setPublicationArtifactError(
        isFR
          ? "L’intérieur et la couverture finale doivent être approuvés avant la persistance des artefacts."
          : "The Interior and Final Cover must be approved before publication-artifact persistence."
      );
      return;
    }

    try {
      setPersistingPublicationArtifacts(true);
      setPublicationArtifactError("");
      setPublicationArtifactMessage(
        isFR
          ? "Revérification et persistance des octets exacts approuvés…"
          : "Reverifying and persisting the exact approved publication bytes…"
      );

      const result = await api.persistTextbookApprovedPublicationArtifacts(projectId, productionRunId);
      if (
        result?.ok !== true || result?.publicationArtifactsPersisted !== true || result?.artifactPersistenceLocked !== true ||
        result?.interiorArtifactPersisted !== true || result?.coverArtifactPersisted !== true || result?.exactPersistedBytesReverified !== true
      ) {
        throw new Error(
          isFR
            ? "Les artefacts de publication approuvés n’ont pas pu être persistés exactement."
            : "The approved publication artifacts could not be persisted exactly."
        );
      }

      setPublicationArtifactsPersisted(true);
      setFinalPublicationPackageReady(false);
      setFinalPublicationPackageMessage("");
      setFinalPublicationPackageError("");
      setPublicationArtifactMessage(
        isFR
          ? "Artefacts de publication approuvés persistés · octets exacts de l’intérieur et de la couverture finale revérifiés. Le paquet final de publication est l’étape suivante."
          : "Approved publication artifacts persisted · exact Interior and Final Cover bytes reverified. Final Publication Package is the next production step."
      );
      setPublicationArtifactError("");
      setMessage(isFR ? "Artefacts de publication approuvés persistés." : "Approved publication artifacts persisted.");
    } catch (err) {
      setPublicationArtifactMessage("");
      setPublicationArtifactError(
        clean(err?.message) ||
        (isFR ? "Impossible de persister les artefacts de publication approuvés." : "Could not persist the approved publication artifacts.")
      );
    } finally {
      setPersistingPublicationArtifacts(false);
    }
  }



  async function approveInterior() {
    const projectId =
      clean(project?.id);

    const review =
      safeObject(
        interiorReview
      );

    if (
      !projectId ||
      review.approvalReady !==
        true ||
      review.finalPageCountLocked ===
        true
    ) {
      setInteriorActionError(
        isFR
          ? "Cet intérieur n’est pas encore admissible à l’approbation."
          : "This Interior is not yet eligible for approval."
      );

      return;
    }

    try {
      setApprovingInterior(
        true
      );

      setInteriorActionError("");
      setError("");

      const result =
        await api
          .approveTextbookInterior(
            projectId,
            {
              productionRunId:
                review
                  .productionRunId,

              pageCount:
                review.pageCount,

              contentSha256:
                review
                  .contentSha256,

              measuredInteriorFingerprint:
                review
                  .measuredInteriorFingerprint,

              reviewCandidateFingerprint:
                review
                  .reviewCandidateFingerprint,
            }
          );

      if (
        result?.ok !==
          true ||
        result
          ?.finalPageCountLocked !==
          true
      ) {
        throw new Error(
          isFR
            ? "L’approbation de l’intérieur n’a pas pu être verrouillée."
            : "Interior Approval could not be locked."
        );
      }

      setInteriorReview(
        (current) => ({
          ...current,

          approvalReady:
            false,

          creatorApprovalStillRequired:
            false,

          finalPageCountLocked:
            true,

          approvedFinalPageCount:
            Number(
              result
                ?.finalPageCount ||
              current
                ?.pageCount ||
              0
            ),
        })
      );

      const success =
        isFR
          ? `Intérieur approuvé · ${result.finalPageCount} pages verrouillées.`
          : `Interior approved · ${result.finalPageCount} pages locked.`;

      setInteriorActionMessage(
        success
      );

      setMessage(
        success
      );

      await loadFinalCoverGeometry(
        result
          ?.productionRunId ||
        review
          .productionRunId
      );
    } catch (err) {
      const actionError =
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’approuver l’intérieur."
            : "Could not approve the Interior."
        );

      setInteriorActionError(
        actionError
      );

      setError(
        actionError
      );
    } finally {
      setApprovingInterior(
        false
      );
    }
  }

  function update(
    group,
    field,
    value
  ) {
    setDraft(
      (current) => ({
        ...current,
        [group]: {
          ...current[group],
          [field]: value,
        },
      })
    );

    setMessage("");
    setError("");
  }

  function handleAssetUploaded(
    asset
  ) {
    if (!asset) return;

    if (
      interiorReview
        ?.finalPageCountLocked ===
          true
    ) {
      setCoverRebindRequired(
        true
      );

      setCoverRebindMessage(
        ""
      );

      setCoverRebindError(
        ""
      );

      setCoverReviewMessage(
        ""
      );

      setCoverReviewError(
        ""
      );

      setCoverReviewBlockDetails(
        null
      );

      setCoverReviewIdentity(
        null
      );

      setCoverApprovalMessage(
        ""
      );

      setCoverApprovalError(
        ""
      );
    }

    setPublicationAssets(
      (current) => [
        ...current.filter(
          (row) =>
            clean(row?.purpose) !==
              clean(asset.purpose)
        ),
        asset,
      ]
    );

    if (
      clean(
        asset.purpose
      ) === "front_cover"
    ) {
      setDraft(
        (current) => ({
          ...current,
          frontCover: {
            ...current.frontCover,
            approach:
              "existing_cover",
          },
        })
      );
    }

    if (
      clean(
        asset.purpose
      ) === "back_cover"
    ) {
      setDraft(
        (current) => ({
          ...current,
          backCover: {
            ...current.backCover,
            approach:
              "existing_cover",
          },
        })
      );
    }
  }

  async function generateInteriorReview() {
    const projectId =
      clean(project?.id);

    if (
      !projectId ||
      readiness.readyCount !==
        readiness.checks.length
    ) {
      setInteriorActionError(
        isFR
          ? "Le livre doit être entièrement prêt avant de générer le PDF intérieur."
          : "The book must be fully ready before generating the Interior Review PDF."
      );

      return;
    }

    let reviewWindow =
      null;

    if (
      typeof window !==
        "undefined" &&
      typeof window.open ===
        "function"
    ) {
      reviewWindow =
        window.open(
          "",
          "_blank"
        );
    }

    try {
      setGeneratingInterior(
        true
      );

      setError("");
      setMessage("");
      setInteriorActionError("");
      setInteriorMismatchDetails(null);
      setInteriorActionMessage(
        isFR
          ? "Préparation du PDF intérieur mesuré…"
          : "Preparing the measured Interior Review PDF…"
      );

      const result =
        await api
          .generateTextbookInteriorReview(
            projectId
          );

      setInteriorReview({
        pageCount:
          Number(
            result?.pageCount ||
            0
          ),

        productionRunId:
          clean(
            result
              ?.productionRunId
          ),

        contentSha256:
          clean(
            result
              ?.contentSha256
          ),

        measuredInteriorFingerprint:
          clean(
            result
              ?.measuredInteriorFingerprint
          ),

        reviewCandidateFingerprint:
          clean(
            result
              ?.reviewCandidateFingerprint
          ),

        creatorApprovalStillRequired:
          result
            ?.creatorApprovalStillRequired ===
          true,

        finalPageCountLocked:
          result
            ?.finalPageCountLocked ===
          true,

        approvalReady:
          result
            ?.approvalReady ===
          true,

        minimumPageCount:
          Number(
            result
              ?.minimumPageCount ||
            0
          ),

        maximumPageCount:
          Number(
            result
              ?.maximumPageCount ||
            0
          ),

        evenPageCount:
          result
            ?.evenPageCount ===
          true,

        withinPrintRange:
          result
            ?.withinPrintRange ===
          true,

        approvalBlockers:
          Array.isArray(
            result
              ?.approvalBlockers
          )
            ? result
                .approvalBlockers
            : [],
      });

      if (
        typeof URL !==
          "undefined" &&
        typeof URL.createObjectURL ===
          "function"
      ) {
        const objectUrl =
          URL.createObjectURL(
            result.blob
          );

        if (
          reviewWindow &&
          !reviewWindow.closed
        ) {
          reviewWindow.location.href =
            objectUrl;
        } else if (
          typeof window !==
            "undefined"
        ) {
          window.open(
            objectUrl,
            "_blank"
          );
        }

        /*
         * Keep the blob URL alive long enough for the new tab
         * to finish loading, then release it.
         */
        if (
          typeof window !==
            "undefined" &&
          typeof window.setTimeout ===
            "function" &&
          typeof URL.revokeObjectURL ===
            "function"
        ) {
          window.setTimeout(
            () =>
              URL.revokeObjectURL(
                objectUrl
              ),
            60000
          );
        }
      }

      const readyMessage =
        isFR
          ? `PDF intérieur prêt${
              Number(
                result
                  ?.pageCount ||
                0
              ) > 0
                ? ` · ${result.pageCount} pages`
                : ""
            }. Vérifiez-le avant toute approbation.`
          : `Interior Review PDF ready${
              Number(
                result
                  ?.pageCount ||
                0
              ) > 0
                ? ` · ${result.pageCount} pages`
                : ""
            }. Review it before approval.`;

      setMessage(
        readyMessage
      );

      setInteriorActionMessage(
        readyMessage
      );
    } catch (err) {
      if (
        reviewWindow &&
        !reviewWindow.closed
      ) {
        reviewWindow.close();
      }

      const actionError =
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de générer le PDF de vérification intérieure."
            : "Could not generate the Interior Review PDF."
        );

      setError(
        actionError
      );

      setInteriorActionError(
        actionError
      );

      setInteriorMismatchDetails(
        err?.data?.code ===
          "textbook_measured_review_assembly_page_mismatch"
          ? (err?.data?.details || null)
          : null
      );

      setInteriorActionMessage("");
    } finally {
      setGeneratingInterior(
        false
      );
    }
  }

  async function save() {
    const revision =
      Number(
        projectPlan?.revision ||
        0
      );

    if (
      !canEdit ||
      typeof onSave !==
        "function"
    ) {
      return;
    }

    if (
      !Number.isInteger(
        revision
      ) ||
      revision < 1
    ) {
      setError(
        isFR
          ? "Rechargez le projet avant d’enregistrer."
          : "Reload the project before saving."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      await onSave(
        draft,
        revision
      );

      setMessage(
        isFR
          ? "Préparation de publication enregistrée."
          : "Publication Preparation saved."
      );
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer la préparation de publication."
            : "Could not save Publication Preparation."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  const trim =
    clean(
      projectPlan
        ?.formatPlan
        ?.trimSize
    ) ||
    (
      isFR
        ? "Non sélectionné"
        : "Not selected"
    );

  const cardStyle = {
    padding: 16,
    border:
      "1px solid #dbe5df",
    borderRadius: 14,
    background: "#fff",
  };

  return (
    <section
      className="ns-card"
      data-textbook-publication-preparation="v2"
      style={{
        padding: 22,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          letterSpacing:
            "0.12em",
          textTransform:
            "uppercase",
          color: "#047857",
        }}
      >
        {isFR
          ? "NEXTSCENES BOOK PRINT · PRÉPARATION"
          : "NEXTSCENES BOOK PRINT · PUBLICATION PREPARATION"}
      </div>

      <h2
        className="ns-textbook-academic-title"
        style={{
          margin:
            "6px 0 8px",
        }}
      >
        {isFR
          ? "Préparer le manuel pour l’impression"
          : "Prepare the Textbook for Print"}
      </h2>

      <p
        className="ns-card-meta"
        style={{
          maxWidth: 860,
        }}
      >
        {isFR
          ? "Ordre de production : couverture avant, pages liminaires, manuscrit principal, éléments de fin, couverture arrière."
          : "Production order: Front Cover, Front Matter, Main Manuscript, Back Matter, Back Cover."}
      </p>

      <div
        data-textbook-publication-readiness="true"
        style={{
          margin:
            "16px 0 20px",
          padding: 14,
          border:
            "1px solid rgba(5,150,105,0.20)",
          borderRadius: 14,
          background:
            "rgba(236,253,245,0.65)",
        }}
      >
        <strong>
          {isFR
            ? "Préparation de publication"
            : "Publication Readiness"}:
          {" "}
          {readiness.readyCount}/{
            readiness.checks.length
          }
        </strong>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 8,
            marginTop: 10,
          }}
        >
          {readiness.checks.map(
            (check) => (
              <div
                key={check.label}
                style={cardStyle}
              >
                <strong>
                  {check.label}
                </strong>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    fontWeight: 800,
                    color:
                      check.ready
                        ? "#047857"
                        : "#92400e",
                  }}
                >
                  {check.ready
                    ? (isFR
                        ? "Prêt"
                        : "Ready")
                    : (isFR
                        ? "À compléter"
                        : "Needs attention")}
                </div>
              </div>
            )
          )}
        </div>

        <div
          className="ns-card-meta"
          style={{
            marginTop: 10,
          }}
        >
          {isFR
            ? `Livre actuel : ${readiness.frontMatter} avant, ${readiness.chapters} chapitre(s), ${readiness.backMatter} fin. Format : ${trim}.`
            : `Current book: ${readiness.frontMatter} Front Matter, ${readiness.chapters} chapter(s), ${readiness.backMatter} Back Matter. Trim: ${trim}.`}
        </div>
      </div>

      <div
        data-textbook-interior-production="v1c2b3"
        style={{
          margin:
            "0 0 24px",
          padding:
            "22px 24px",
          border:
            "2px solid #0b703d",
          borderRadius: 16,
          background:
            "linear-gradient(135deg, rgba(236,253,245,0.98), rgba(255,251,235,0.96))",
          boxShadow:
            "0 8px 22px rgba(6,95,70,0.10)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems:
              "flex-start",
            justifyContent:
              "space-between",
            gap: 16,
            flexWrap:
              "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing:
                  "0.12em",
                textTransform:
                  "uppercase",
                color:
                  "#047857",
                marginBottom: 5,
              }}
            >
              {isFR
                ? "NEXTSCENES BOOK PRINT · ÉTAPE SUIVANTE"
                : "NEXTSCENES BOOK PRINT · NEXT PRODUCTION STEP"}
            </div>

            <h3
              className="ns-textbook-academic-title"
              style={{
                margin:
                  "0 0 8px",
                fontSize: 24,
              }}
            >
              {isFR
                ? "Production intérieure"
                : "Interior Production"}
            </h3>
          </div>

          <div
            data-textbook-interior-production-ready="true"
            style={{
              borderRadius: 999,
              padding:
                "7px 12px",
              fontSize: 12,
              fontWeight: 900,
              background:
                readiness.readyCount ===
                  readiness.checks.length
                  ? "#dcfce7"
                  : "#fef3c7",
              color:
                readiness.readyCount ===
                  readiness.checks.length
                  ? "#065f46"
                  : "#92400e",
              border:
                readiness.readyCount ===
                  readiness.checks.length
                  ? "1px solid #86efac"
                  : "1px solid #fcd34d",
            }}
          >
            {readiness.readyCount ===
            readiness.checks.length
              ? (isFR
                  ? `${readiness.readyCount}/${readiness.checks.length} · PRÊT À GÉNÉRER`
                  : `${readiness.readyCount}/${readiness.checks.length} · READY TO GENERATE`)
              : !readiness
                    .publicationManuscriptLocked
                ? (isFR
                    ? `${readiness.readyCount}/${readiness.checks.length} · VERROUILLEZ D’ABORD LE MANUSCRIT`
                    : `${readiness.readyCount}/${readiness.checks.length} · LOCK MANUSCRIPT FIRST`)
                : (isFR
                    ? `${readiness.readyCount}/${readiness.checks.length} · À COMPLÉTER`
                    : `${readiness.readyCount}/${readiness.checks.length} · COMPLETE PREPARATION`)}
          </div>
        </div>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "0 0 16px",
            maxWidth: 820,
            fontSize: 15,
            lineHeight: 1.6,
          }}
        >
          {isFR
            ? "Créez maintenant le PDF intérieur physique mesuré du livre enregistré. Le PDF s’ouvrira dans un nouvel onglet pour votre vérification avant approbation."
            : "Create the measured physical Interior Review PDF from the saved book. The PDF will open in a new tab so you can inspect the actual pages before approval."}
        </p>

        {!readiness
          .publicationManuscriptLocked ? (
          <div
            data-textbook-publication-lock-required="true"
            style={{
              margin:
                "0 0 14px",
              padding:
                "12px 14px",
              borderRadius: 12,
              background:
                "#fffbeb",
              border:
                "1px solid #fcd34d",
              color:
                "#92400e",
              fontWeight: 700,
            }}
          >
            <div>
              {isFR
                ? "Révisez d’abord le livre et verrouillez le manuscrit de publication. Le PDF intérieur ne peut pas être généré à partir du Canon de travail."
                : "Review the book and lock its publication manuscript first. Interior Production cannot generate from the working Canon."}
            </div>

            {typeof onOpenReview ===
              "function" ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={
                  onOpenReview
                }
                style={{
                  marginTop: 10,
                }}
              >
                {isFR
                  ? "Réviser et verrouiller le manuscrit"
                  : "Review & Lock Manuscript"}
              </button>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          className="ns-button ns-button-primary"
          data-textbook-generate-interior-review="true"
          disabled={
            generatingInterior ||
            readiness.readyCount !==
              readiness.checks.length
          }
          onClick={
            generateInteriorReview
          }
          style={{
            minHeight: 46,
            padding:
              "10px 20px",
            fontSize: 15,
            fontWeight: 800,
            boxShadow:
              "0 4px 12px rgba(6,95,70,0.22)",
          }}
        >
          {generatingInterior
            ? (isFR
                ? "Génération du PDF intérieur…"
                : "Generating Interior Review PDF…")
            : (isFR
                ? "Générer et ouvrir le PDF intérieur"
                : "Generate & Open Interior Review PDF")}
        </button>

        <div
          aria-live="polite"
          style={{
            marginTop: 12,
          }}
        >
          {generatingInterior ? (
            <div
              data-textbook-interior-progress="true"
              style={{
                padding:
                  "10px 12px",
                borderRadius: 10,
                background:
                  "#ecfdf5",
                border:
                  "1px solid #a7f3d0",
                color:
                  "#065f46",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {isFR
                ? "Génération en cours. Mesure des pages, rendu PDF et préparation de la copie de vérification…"
                : "Generation in progress. Measuring pages, rendering the PDF, and preparing the private review copy…"}
            </div>
          ) : null}

          {!generatingInterior &&
          interiorActionError ? (
            <div
              data-textbook-interior-error="true"
              style={{
                padding:
                  "10px 12px",
                borderRadius: 10,
                background:
                  "#fef2f2",
                border:
                  "1px solid #fecaca",
                color:
                  "#991b1b",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              <strong>
                {isFR
                  ? "Le PDF n’a pas été généré."
                  : "The PDF was not generated."}
              </strong>
              {" "}
              {interiorActionError}
            </div>
          ) : null}

          {!generatingInterior &&
          interiorActionError &&
          interiorMismatchDetails ? (
            <div
              data-textbook-interior-mismatch-details="true"
              style={{
                marginTop: 10,
                padding: "12px 14px",
                borderRadius: 10,
                background: "#fff7ed",
                border: "1px solid #fdba74",
                color: "#7c2d12",
                fontSize: 13,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  marginBottom: 6,
                }}
              >
                {isFR
                  ? "Différence de pagination détectée"
                  : "Pagination difference detected"}
              </div>

              <div>
                {isFR
                  ? "Révision du livre verrouillée"
                  : "Locked Book Review"}:
                {" "}
                <strong>
                  {Number(
                    interiorMismatchDetails
                      ?.expectedReviewPageCount ||
                    0
                  )}
                </strong>
                {" pages · "}
                {isFR
                  ? "Moteur PDF"
                  : "PDF engine"}:
                {" "}
                <strong>
                  {Number(
                    interiorMismatchDetails
                      ?.measuredPageCount ||
                    0
                  )}
                </strong>
                {" pages"}
              </div>

              {Array.isArray(
                interiorMismatchDetails
                  ?.unitMismatches
              ) &&
              interiorMismatchDetails
                .unitMismatches
                .length > 0 ? (
                <div
                  style={{
                    marginTop: 8,
                    display: "grid",
                    gap: 4,
                  }}
                >
                  {interiorMismatchDetails
                    .unitMismatches
                    .map(
                      (row, index) => (
                        <div
                          key={`${row?.unitId || "unit"}-${index}`}
                        >
                          <strong>
                            {row?.title ||
                              row?.unitId ||
                              (
                                isFR
                                  ? "Élément"
                                  : "Book component"
                              )}
                          </strong>
                          {": "}
                          {isFR ? "révision" : "Review"}
                          {" "}
                          {Number(row?.expected || 0)}
                          {" · PDF "}
                          {Number(row?.actual || 0)}
                        </div>
                      )
                    )}
                </div>
              ) : null}
            </div>
          ) : null}

          {!generatingInterior &&
          !interiorActionError &&
          interiorActionMessage ? (
            <div
              data-textbook-interior-message="true"
              style={{
                padding:
                  "10px 12px",
                borderRadius: 10,
                background:
                  "#ecfdf5",
                border:
                  "1px solid #a7f3d0",
                color:
                  "#065f46",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {interiorActionMessage}
            </div>
          ) : null}
        </div>

        {readiness.readyCount !==
        readiness.checks.length ? (
          <div
            className="ns-card-meta"
            style={{
              marginTop: 10,
              fontWeight: 700,
            }}
          >
            {isFR
              ? "Terminez d’abord les cinq éléments de préparation de publication."
              : "Complete all five Publication Preparation checks first."}
          </div>
        ) : null}

        {interiorReview ? (
          <div
            data-textbook-interior-review-state="true"
            className="ns-card-meta"
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop:
                "1px solid rgba(6,95,70,0.18)",
            }}
          >
            <strong>
              {isFR
                ? "PDF de vérification généré"
                : "Interior Review generated"}
            </strong>

            {Number(
              interiorReview
                .pageCount
            ) > 0
              ? ` · ${
                  interiorReview
                    .pageCount
                } pages`
              : ""}

            <br />

            {interiorReview
              .approvalReady
              ? (
                  isFR
                    ? "Prêt pour l’approbation de l’intérieur · nombre final de pages non encore verrouillé."
                    : "Ready for Interior Approval · final page count not yet locked."
                )
              : (
                  isFR
                    ? "L’approbation de l’intérieur n’est pas encore disponible."
                    : "Interior Approval is not yet available."
                )}

            {interiorReview
              .finalPageCountLocked ? (
              <div
                data-textbook-interior-approval-locked="true"
                style={{
                  marginTop: 8,
                  padding:
                    "10px 12px",
                  borderRadius: 10,
                  background:
                    "#ecfdf5",
                  border:
                    "1px solid #86efac",
                  color:
                    "#065f46",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {isFR
                  ? `Intérieur approuvé · ${
                      Number(
                        interiorReview
                          .approvedFinalPageCount ||
                        interiorReview
                          .pageCount ||
                        0
                      )
                    } pages verrouillées.`
                  : `Interior approved · ${
                      Number(
                        interiorReview
                          .approvedFinalPageCount ||
                        interiorReview
                          .pageCount ||
                        0
                      )
                    } pages locked.`}
              </div>
            ) : null}

            {interiorReview
              .approvalReady &&
            !interiorReview
              .finalPageCountLocked ? (
              <button
                type="button"
                className="ns-button ns-button-primary"
                data-textbook-approve-interior="true"
                disabled={
                  !canEdit ||
                  approvingInterior
                }
                onClick={
                  approveInterior
                }
                style={{
                  marginTop: 12,
                  minHeight: 46,
                  padding:
                    "10px 20px",
                  fontSize: 15,
                  fontWeight: 900,
                  boxShadow:
                    "0 4px 12px rgba(6,95,70,0.22)",
                }}
              >
                {approvingInterior
                  ? (
                      isFR
                        ? "Approbation…"
                        : "Approving…"
                    )
                  : (
                      isFR
                        ? "Approuver l’intérieur"
                        : "Approve Interior"
                    )}
              </button>
            ) : null}

            {!interiorReview
              .approvalReady &&
            !interiorReview
              .finalPageCountLocked ? (
              <div
                data-textbook-interior-approval-readiness="blocked"
                style={{
                  marginTop: 8,
                  padding:
                    "10px 12px",
                  borderRadius: 10,
                  background:
                    "#fffbeb",
                  border:
                    "1px solid #fcd34d",
                  color:
                    "#92400e",
                  fontSize: 12,
                  lineHeight: 1.55,
                }}
              >
                {Number(
                  interiorReview
                    .minimumPageCount
                ) > 0 &&
                Number(
                  interiorReview
                    .maximumPageCount
                ) > 0 ? (
                  <div>
                    {isFR
                      ? "Plage de pages du profil d’impression sélectionné"
                      : "Selected print profile page range"}:
                    {" "}
                    <strong>
                      {
                        interiorReview
                          .minimumPageCount
                      }
                      {"–"}
                      {
                        interiorReview
                          .maximumPageCount
                      }
                    </strong>
                    {" · "}
                    {isFR
                      ? "intérieur actuel"
                      : "current interior"}:
                    {" "}
                    <strong>
                      {
                        interiorReview
                          .pageCount
                      }
                    </strong>
                  </div>
                ) : null}

                {!interiorReview
                  .withinPrintRange ? (
                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    {isFR
                      ? "Le nombre de pages doit d’abord entrer dans la plage validée du profil d’impression."
                      : "The page count must first fall within the validated range for the selected print profile."}
                  </div>
                ) : null}

                {!interiorReview
                  .evenPageCount ? (
                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    {isFR
                      ? "La paire physique finale n’est pas encore complète. NextScenes doit la résoudre avant le verrouillage final."
                      : "The final physical page pair is not yet complete. NextScenes must resolve it before final page-count lock."}
                  </div>
                ) : null}
              </div>
            ) : (
              <div
                data-textbook-interior-approval-readiness="ready"
                style={{
                  marginTop: 8,
                  color:
                    "#065f46",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {isFR
                  ? "Le PDF mesuré satisfait les règles de nombre de pages du profil d’impression sélectionné."
                  : "The measured PDF satisfies the page-count rules for the selected print profile."}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {interiorReview
        ?.finalPageCountLocked ===
          true ? (
        <div
          data-textbook-final-cover-production="v1a"
          style={{
            margin:
              "0 0 24px",
            padding:
              "22px 24px",
            border:
              "2px solid #334155",
            borderRadius: 16,
            background:
              "linear-gradient(135deg, rgba(248,250,252,0.98), rgba(240,253,250,0.96))",
            boxShadow:
              "0 8px 22px rgba(15,23,42,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "flex-start",
              gap: 16,
              flexWrap:
                "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing:
                    "0.12em",
                  textTransform:
                    "uppercase",
                  color:
                    "#475569",
                  marginBottom: 5,
                }}
              >
                {isFR
                  ? "NEXTSCENES BOOK PRINT · COUVERTURE FINALE"
                  : "NEXTSCENES BOOK PRINT · FINAL COVER PRODUCTION"}
              </div>

              <h3
                className="ns-textbook-academic-title"
                style={{
                  margin:
                    "0 0 8px",
                  fontSize: 24,
                }}
              >
                {isFR
                  ? "Géométrie physique verrouillée"
                  : "Locked Physical Cover Geometry"}
              </h3>
            </div>

            <div
              style={{
                borderRadius: 999,
                padding:
                  "7px 12px",
                fontSize: 12,
                fontWeight: 900,
                background:
                  coverGeometry
                    ?.coverGeometryReady ===
                      true
                    ? "#dcfce7"
                    : "#fef3c7",
                color:
                  coverGeometry
                    ?.coverGeometryReady ===
                      true
                    ? "#065f46"
                    : "#92400e",
                border:
                  coverGeometry
                    ?.coverGeometryReady ===
                      true
                    ? "1px solid #86efac"
                    : "1px solid #fcd34d",
              }}
            >
              {coverGeometry
                ?.coverGeometryReady ===
                  true
                ? (
                    isFR
                      ? "GÉOMÉTRIE PRÊTE"
                      : "GEOMETRY READY"
                  )
                : (
                    isFR
                      ? "CALCUL À EFFECTUER"
                      : "CALCULATE GEOMETRY"
                  )}
            </div>
          </div>

          <p
            className="ns-card-meta"
            style={{
              maxWidth: 860,
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? "La largeur du dos et l’enveloppe complète sont dérivées côté serveur du nombre final de pages verrouillé et du profil d’impression approuvé. Le navigateur ne fournit ni nombre de pages ni réglages d’impression."
              : "Spine width and the full print wrap are derived server-side from the locked final physical page count and approved print profile. The browser supplies neither page count nor print settings."}
          </p>

          <button
            type="button"
            className="ns-button"
            data-textbook-final-cover-geometry-action="true"
            disabled={
              !canEdit ||
              loadingCoverGeometry
            }
            onClick={() =>
              loadFinalCoverGeometry(
                interiorReview
                  ?.productionRunId
              )
            }
          >
            {loadingCoverGeometry
              ? (
                  isFR
                    ? "Calcul…"
                    : "Calculating…"
                )
              : coverGeometry
                  ?.coverGeometryReady ===
                    true
                ? (
                    isFR
                      ? "Recalculer la géométrie"
                      : "Refresh Physical Geometry"
                  )
                : (
                    isFR
                      ? "Calculer la géométrie finale"
                      : "Calculate Final Cover Geometry"
                  )}
          </button>

          {coverGeometryError ? (
            <div
              style={{
                marginTop: 10,
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              {coverGeometryError}
            </div>
          ) : null}

          {coverGeometry
            ?.coverGeometryReady ===
              true ? (
            <div
              data-textbook-final-cover-geometry="ready"
              style={{
                marginTop: 16,
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 10,
              }}
            >
              {[
                [
                  isFR
                    ? "Pages finales"
                    : "Final pages",
                  coverGeometry
                    .finalPageCount,
                ],
                [
                  isFR
                    ? "Format"
                    : "Trim",
                  `${coverGeometry.trimWidthInches} x ${coverGeometry.trimHeightInches} in`,
                ],
                [
                  isFR
                    ? "Dos"
                    : "Spine width",
                  `${coverGeometry.spineWidthInches} in`,
                ],
                [
                  isFR
                    ? "Enveloppe complète"
                    : "Full wrap",
                  `${coverGeometry.fullCoverWidthInches} x ${coverGeometry.fullCoverHeightInches} in`,
                ],
              ].map(
                ([label, value]) => (
                  <div
                    key={label}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      border:
                        "1px solid #cbd5e1",
                      background:
                        "#ffffff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#64748b",
                        textTransform:
                          "uppercase",
                        letterSpacing:
                          "0.05em",
                      }}
                    >
                      {label}
                    </div>

                    <strong
                      style={{
                        display: "block",
                        marginTop: 4,
                        color: "#0f172a",
                      }}
                    >
                      {value}
                    </strong>
                  </div>
                )
              )}
            </div>
          ) : null}

          {coverGeometry
            ?.coverGeometryReady ===
              true ? (
            <div
              data-textbook-cover-review-v1b="true"
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop:
                  "1px solid #cbd5e1",
              }}
            >
              {coverRebindRequired ? (
                <div
                  data-textbook-cover-rebind-required="true"
                  style={{
                    marginBottom: 12,
                    padding:
                      "12px 14px",
                    borderRadius: 10,
                    background:
                      "#fff7ed",
                    border:
                      "1px solid #fdba74",
                    color:
                      "#7c2d12",
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 900,
                    }}
                  >
                    {isFR
                      ? "Les couvertures ont changé après l’approbation de l’intérieur."
                      : "Cover artwork changed after Interior approval."}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                    }}
                  >
                    {isFR
                      ? "L’intérieur approuvé et son nombre de pages verrouillé restent inchangés. Liez uniquement les couvertures actuelles avant la vérification finale."
                      : "The approved Interior and its locked page count remain unchanged. Bind only the current cover artwork before final Cover Review."}
                  </div>

                  <button
                    type="button"
                    className="ns-button"
                    data-textbook-rebind-cover-artwork="true"
                    disabled={
                      !canEdit ||
                      rebindingCoverArtwork
                    }
                    onClick={
                      rebindCurrentCoverArtwork
                    }
                    style={{
                      marginTop: 10,
                      fontWeight: 900,
                    }}
                  >
                    {rebindingCoverArtwork
                      ? (
                          isFR
                            ? "Vérification des couvertures…"
                            : "Verifying Covers…"
                        )
                      : (
                          isFR
                            ? "Utiliser les couvertures actuelles"
                            : "Use Current Covers for Cover Review"
                        )}
                  </button>
                </div>
              ) : null}

              {coverRebindMessage ? (
                <div
                  data-textbook-cover-rebind-message="true"
                  style={{
                    marginBottom: 12,
                    padding:
                      "10px 12px",
                    borderRadius: 10,
                    background:
                      "#ecfdf5",
                    border:
                      "1px solid #a7f3d0",
                    color:
                      "#065f46",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {coverRebindMessage}
                </div>
              ) : null}

              {coverRebindError ? (
                <div
                  data-textbook-cover-rebind-error="true"
                  style={{
                    marginBottom: 12,
                    padding:
                      "10px 12px",
                    borderRadius: 10,
                    background:
                      "#fef2f2",
                    border:
                      "1px solid #fecaca",
                    color:
                      "#991b1b",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {coverRebindError}
                </div>
              ) : null}

              <button
                type="button"
                className="ns-button ns-button-primary"
                data-textbook-generate-cover-review="true"
                disabled={
                  !canEdit ||
                  generatingCoverReview ||
                  coverRebindRequired
                }
                onClick={
                  generateCoverReview
                }
                style={{
                  minHeight: 46,
                  padding:
                    "10px 20px",
                  fontSize: 15,
                  fontWeight: 900,
                  boxShadow:
                    "0 4px 12px rgba(6,95,70,0.22)",
                }}
              >
                {generatingCoverReview
                  ? (
                      isFR
                        ? "Génération du PDF de couverture…"
                        : "Generating Cover Review PDF…"
                    )
                  : (
                      isFR
                        ? "Générer et ouvrir le PDF de vérification de couverture"
                        : "Generate & Open Cover Review PDF"
                    )}
              </button>

              {coverReviewMessage ? (
                <div
                  data-textbook-cover-review-message="true"
                  style={{
                    marginTop: 10,
                    padding:
                      "10px 12px",
                    borderRadius: 10,
                    background:
                      "#ecfdf5",
                    border:
                      "1px solid #a7f3d0",
                    color:
                      "#065f46",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {coverReviewMessage}
                </div>
              ) : null}

              {coverReviewError ? (
                <div
                  data-textbook-cover-review-error="true"
                  style={{
                    marginTop: 10,
                    padding:
                      "12px 14px",
                    borderRadius: 10,
                    background:
                      "#fff7ed",
                    border:
                      "1px solid #fdba74",
                    color:
                      "#7c2d12",
                    fontSize: 13,
                    lineHeight: 1.55,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 900,
                      marginBottom: 6,
                    }}
                  >
                    {isFR
                      ? "Le PDF de couverture n’a pas été généré."
                      : "The Cover Review PDF was not generated."}
                  </div>

                  <div>
                    {coverReviewError}
                  </div>

                  {Array.isArray(
                    coverReviewBlockDetails
                      ?.panels
                  ) &&
                  coverReviewBlockDetails
                    .panels
                    .length > 0 ? (
                    <div
                      data-textbook-cover-review-preflight="blocked"
                      style={{
                        marginTop: 10,
                      }}
                    >
                      <div>
                        {isFR
                          ? "Format physique verrouillé"
                          : "Locked physical trim"}:
                        {" "}
                        <strong>
                          {Number(
                            coverReviewBlockDetails
                              ?.trimWidthInches ||
                            0
                          )}
                          {" × "}
                          {Number(
                            coverReviewBlockDetails
                              ?.trimHeightInches ||
                            0
                          )}
                          {" in"}
                        </strong>
                        {" · "}
                        {isFR
                          ? "recadrage automatique maximal"
                          : "maximum automatic crop"}:
                        {" "}
                        <strong>
                          {Number(
                            coverReviewBlockDetails
                              ?.maximumCropPercent ||
                            0
                          ).toFixed(0)}
                          {"%"}
                        </strong>
                      </div>

                      {coverReviewBlockDetails
                        .panels
                        .map(
                          (panel) => (
                            <div
                              key={
                                clean(
                                  panel?.purpose
                                ) ||
                                clean(
                                  panel?.label
                                )
                              }
                              style={{
                                marginTop: 6,
                              }}
                            >
                              <strong>
                                {clean(
                                  panel?.label
                                ) ||
                                  clean(
                                    panel?.purpose
                                  )}
                              </strong>
                              {": "}
                              {Number(
                                panel
                                  ?.widthPixels ||
                                0
                              )}
                              {" × "}
                              {Number(
                                panel
                                  ?.heightPixels ||
                                0
                              )}
                              {" px · "}
                              {isFR
                                ? "recadrage estimé"
                                : "estimated crop"}:
                              {" "}
                              <strong>
                                {Number(
                                  panel
                                    ?.estimatedCropPercent ||
                                  0
                                ).toFixed(2)}
                                {"%"}
                              </strong>
                            </div>
                          )
                        )}

                      <div
                        style={{
                          marginTop: 8,
                          fontWeight: 800,
                        }}
                      >
                        {isFR
                          ? "Remplacez les couvertures complètes par des œuvres préparées aux proportions du format sélectionné avant la vérification finale."
                          : "Replace complete-panel cover artwork with artwork prepared to the selected trim proportions before final Cover Review."}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {coverApproved ? (
                <div
                  data-textbook-cover-approval-locked="true"
                  style={{
                    marginTop: 12,
                    padding:
                      "12px 14px",
                    borderRadius: 10,
                    background:
                      "#ecfdf5",
                    border:
                      "1px solid #86efac",
                    color:
                      "#065f46",
                    fontWeight: 800,
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {coverApprovalMessage ||
                    (
                      isFR
                        ? "Couverture finale approuvée · identité exacte verrouillée."
                        : "Final Cover approved · exact reviewed identity locked."
                    )}
                </div>
              ) : (
                coverReviewIdentity ? (
                  <div
                    data-textbook-cover-approval-ready="true"
                    style={{
                      marginTop: 12,
                      padding:
                        "12px 14px",
                      borderRadius: 10,
                      background:
                        "#f0fdf4",
                      border:
                        "1px solid #bbf7d0",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        color:
                          "#14532d",
                        marginBottom: 8,
                      }}
                    >
                      {isFR
                        ? "Le PDF exact de la couverture est prêt pour votre décision."
                        : "The exact reviewed Final Cover is ready for your decision."}
                    </div>

                    <button
                      type="button"
                      className="ns-button ns-button-primary"
                      data-textbook-approve-cover="true"
                      disabled={
                        !canEdit ||
                        approvingCover ||
                        coverRebindRequired
                      }
                      onClick={
                        approveCover
                      }
                    >
                      {approvingCover
                        ? (
                            isFR
                              ? "Approbation de la couverture…"
                              : "Approving Final Cover…"
                          )
                        : (
                            isFR
                              ? "Approuver cette couverture finale"
                              : "Approve This Final Cover"
                          )}
                    </button>
                  </div>
                ) : null
              )}

              {!coverApproved &&
              coverApprovalMessage ? (
                <div
                  data-textbook-cover-approval-message="true"
                  style={{
                    marginTop: 10,
                    color:
                      "#065f46",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {coverApprovalMessage}
                </div>
              ) : null}

              {coverApprovalError ? (
                <div
                  data-textbook-cover-approval-error="true"
                  style={{
                    marginTop: 10,
                    padding:
                      "10px 12px",
                    borderRadius: 10,
                    background:
                      "#fef2f2",
                    border:
                      "1px solid #fecaca",
                    color:
                      "#991b1b",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {coverApprovalError}
                </div>
              ) : null}

              {coverApproved &&
              !publicationArtifactsPersisted ? (
                <div
                  data-textbook-publication-artifact-persistence-ready="true"
                  style={{
                    marginTop: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#1e3a8a", marginBottom: 8 }}>
                    {isFR
                      ? "L’intérieur et la couverture finale approuvés sont prêts pour la persistance exacte des artefacts."
                      : "The approved Interior and Final Cover are ready for exact publication-artifact persistence."}
                  </div>

                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    data-textbook-persist-approved-publication-artifacts="true"
                    disabled={!canEdit || persistingPublicationArtifacts}
                    onClick={persistApprovedPublicationArtifacts}
                  >
                    {persistingPublicationArtifacts
                      ? (isFR ? "Persistance des artefacts…" : "Persisting Approved Artifacts…")
                      : (isFR ? "Persister les artefacts de publication approuvés" : "Persist Approved Publication Artifacts")}
                  </button>
                </div>
              ) : null}

              {publicationArtifactsPersisted ? (
                <div
                  data-textbook-publication-artifact-persistence-locked="true"
                  style={{
                    marginTop: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#ecfdf5",
                    border: "1px solid #86efac",
                    color: "#065f46",
                    fontWeight: 800,
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {publicationArtifactMessage ||
                    (isFR
                      ? "Artefacts de publication approuvés persistés · octets exacts revérifiés."
                      : "Approved publication artifacts persisted · exact bytes reverified.")}
                </div>
              ) : (
                publicationArtifactMessage ? (
                  <div data-textbook-publication-artifact-persistence-message="true" style={{ marginTop: 10, color: "#1e3a8a", fontWeight: 700, fontSize: 13 }}>
                    {publicationArtifactMessage}
                  </div>
                ) : null
              )}

              {publicationArtifactError ? (
                <div
                  data-textbook-publication-artifact-persistence-error="true"
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {publicationArtifactError}
                </div>
              ) : null}

              {publicationArtifactsPersisted &&
              !finalPublicationPackageReady ? (
                <div
                  data-textbook-final-publication-package-ready-to-build="true"
                  style={{
                    marginTop: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fdba74",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#9a3412", marginBottom: 8 }}>
                    {isFR
                      ? "Les artefacts approuvés persistés sont prêts pour la construction du paquet final de publication."
                      : "The persisted approved artifacts are ready for Final Publication Package construction."}
                  </div>

                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    data-textbook-build-final-publication-package="true"
                    disabled={!canEdit || buildingFinalPublicationPackage}
                    onClick={buildFinalPublicationPackage}
                  >
                    {buildingFinalPublicationPackage
                      ? (isFR ? "Construction du paquet final…" : "Building Final Publication Package…")
                      : (isFR ? "Construire le paquet final de publication" : "Build Final Publication Package")}
                  </button>
                </div>
              ) : null}

              {finalPublicationPackageReady ? (
                <div
                  data-textbook-final-publication-package-locked="true"
                  style={{
                    marginTop: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#ecfdf5",
                    border: "1px solid #86efac",
                    color: "#065f46",
                    fontWeight: 800,
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}
                >
                  {finalPublicationPackageMessage ||
                    (isFR
                      ? "Paquet final de publication prêt · artefacts exacts revérifiés."
                      : "Final Publication Package ready · exact persisted artifacts reverified.")}
                  <div style={{ marginTop: 6 }}>
                    {isFR
                      ? "Étape suivante : vérifier ou télécharger les fichiers de publication."
                      : "Next: review or download the publication files."}
                  </div>
                </div>
              ) : (
                finalPublicationPackageMessage ? (
                  <div data-textbook-final-publication-package-message="true" style={{ marginTop: 10, color: "#9a3412", fontWeight: 700, fontSize: 13 }}>
                    {finalPublicationPackageMessage}
                  </div>
                ) : null
              )}


              {finalPublicationPackageReady ? (
                <div
                  data-textbook-final-publication-files-ready="true"
                  style={{
                    marginTop: 12,
                    padding: "14px",
                    borderRadius: 10,
                    background: "#f8fafc",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#0f172a",
                    }}
                  >
                    {isFR
                      ? "Fichiers finaux de publication"
                      : "Final Publication Files"}
                  </div>

                  <div
                    className="ns-card-meta"
                    style={{
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {isFR
                      ? "Chaque ouverture ou téléchargement relit le PDF persistant et revérifie son identité exacte avant livraison."
                      : "Each review or download rereads the durable PDF and reverifies its exact byte identity before delivery."}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 10,
                      marginTop: 12,
                    }}
                  >
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Intérieur final"
                          : "Final Interior PDF"}
                      </strong>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          marginTop: 10,
                        }}
                      >
                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          data-textbook-review-final-interior="true"
                          disabled={
                            publicationFileLoadingRole !==
                              ""
                          }
                          onClick={() =>
                            reviewFinalPublicationFile(
                              "interior"
                            )
                          }
                        >
                          {publicationFileLoadingRole ===
                            "review-interior"
                            ? (
                                isFR
                                  ? "Ouverture…"
                                  : "Opening…"
                              )
                            : (
                                isFR
                                  ? "Vérifier le PDF intérieur"
                                  : "Review Final Interior"
                              )}
                        </button>

                        <button
                          type="button"
                          className="ns-button ns-button-primary"
                          data-textbook-download-final-interior="true"
                          disabled={
                            publicationFileLoadingRole !==
                              ""
                          }
                          onClick={() =>
                            downloadFinalPublicationFile(
                              "interior"
                            )
                          }
                        >
                          {publicationFileLoadingRole ===
                            "download-interior"
                            ? (
                                isFR
                                  ? "Téléchargement…"
                                  : "Downloading…"
                              )
                            : (
                                isFR
                                  ? "Télécharger l’intérieur"
                                  : "Download Final Interior"
                              )}
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Couverture finale"
                          : "Final Cover PDF"}
                      </strong>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          marginTop: 10,
                        }}
                      >
                        <button
                          type="button"
                          className="ns-button ns-button-outline"
                          data-textbook-review-final-cover="true"
                          disabled={
                            publicationFileLoadingRole !==
                              ""
                          }
                          onClick={() =>
                            reviewFinalPublicationFile(
                              "cover"
                            )
                          }
                        >
                          {publicationFileLoadingRole ===
                            "review-cover"
                            ? (
                                isFR
                                  ? "Ouverture…"
                                  : "Opening…"
                              )
                            : (
                                isFR
                                  ? "Vérifier le PDF de couverture"
                                  : "Review Final Cover"
                              )}
                        </button>

                        <button
                          type="button"
                          className="ns-button ns-button-primary"
                          data-textbook-download-final-cover="true"
                          disabled={
                            publicationFileLoadingRole !==
                              ""
                          }
                          onClick={() =>
                            downloadFinalPublicationFile(
                              "cover"
                            )
                          }
                        >
                          {publicationFileLoadingRole ===
                            "download-cover"
                            ? (
                                isFR
                                  ? "Téléchargement…"
                                  : "Downloading…"
                              )
                            : (
                                isFR
                                  ? "Télécharger la couverture"
                                  : "Download Final Cover"
                              )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {publicationFileError ? (
                    <div
                      role="alert"
                      data-textbook-final-publication-file-error="true"
                      style={{
                        marginTop: 10,
                        color: "#991b1b",
                        fontWeight: 700,
                      }}
                    >
                      {publicationFileError}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {finalPublicationPackageError ? (
                <div
                  data-textbook-final-publication-package-error="true"
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {finalPublicationPackageError}
                </div>
              ) : null}

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 10,
                  fontWeight: 700,
                }}
              >
                {isFR
                  ? "Le serveur utilise uniquement le nombre de pages verrouillé, le profil d’impression approuvé et les révisions exactes des couvertures figées dans la source de production."
                  : "The server uses only the locked page count, approved print profile, and exact cover-asset revisions frozen into the production source."}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {assetsLoading ? (
        <p className="ns-card-meta">
          {isFR
            ? "Chargement des couvertures enregistrées…"
            : "Loading saved cover assets…"}
        </p>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        <article style={cardStyle}>
          <h3 className="ns-textbook-academic-title">
            1 · {isFR
              ? "Couverture avant"
              : "Front Cover"}
          </h3>

          <label>
            <strong>
              {isFR
                ? "Approche"
                : "Approach"}
            </strong>

            <select
              className="ns-input"
              value={
                draft
                  .frontCover
                  .approach
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "frontCover",
                  "approach",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir…"
                  : "Choose…"}
              </option>
              <option value="nextscenes_layout">
                {isFR
                  ? "Mise en page NextScenes"
                  : "NextScenes cover layout"}
              </option>
              <option value="existing_cover">
                {isFR
                  ? "Couverture existante"
                  : "Existing cover artwork"}
              </option>
            </select>
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Titre"
                : "Title"}
            </strong>

            <input
              className="ns-input"
              value={
                draft
                  .frontCover
                  .title
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "frontCover",
                  "title",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Sous-titre"
                : "Subtitle"}
            </strong>

            <input
              className="ns-input"
              value={
                draft
                  .frontCover
                  .subtitle
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "frontCover",
                  "subtitle",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Nom de l’auteur"
                : "Author name"}
            </strong>

            <input
              className="ns-input"
              value={
                draft
                  .frontCover
                  .authorName
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "frontCover",
                  "authorName",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <CoverUpload
            isFR={isFR}
            projectId={
              clean(project?.id)
            }
            purpose="front_cover"
            label={
              isFR
                ? "Image de couverture avant"
                : "Front-cover artwork"
            }
            asset={frontAsset}
            enabled={
              canEdit &&
              !coverApproved &&
              draft
                .frontCover
                .approach ===
                  "existing_cover"
            }
            onUploaded={
              handleAssetUploaded
            }
          />
        </article>

        <article style={cardStyle}>
          <h3 className="ns-textbook-academic-title">
            2 · {isFR
              ? "Couverture arrière"
              : "Back Cover"}
          </h3>

          <label>
            <strong>
              {isFR
                ? "Approche"
                : "Approach"}
            </strong>

            <select
              className="ns-input"
              value={
                draft
                  .backCover
                  .approach
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "backCover",
                  "approach",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir…"
                  : "Choose…"}
              </option>
              <option value="nextscenes_layout">
                {isFR
                  ? "Mise en page NextScenes"
                  : "NextScenes back-cover layout"}
              </option>
              <option value="existing_cover">
                {isFR
                  ? "Couverture existante"
                  : "Existing back-cover artwork"}
              </option>
            </select>
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Résumé"
                : "Blurb / description"}
            </strong>

            <textarea
              className="ns-input"
              rows={5}
              value={
                draft
                  .backCover
                  .blurb
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "backCover",
                  "blurb",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "À propos de l’auteur"
                : "Author bio"}
            </strong>

            <textarea
              className="ns-input"
              rows={4}
              value={
                draft
                  .backCover
                  .authorBio
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "backCover",
                  "authorBio",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>ISBN</strong>

            <input
              className="ns-input"
              value={
                draft
                  .backCover
                  .isbn
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "backCover",
                  "isbn",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            />
          </label>

          <CoverUpload
            isFR={isFR}
            projectId={
              clean(project?.id)
            }
            purpose="back_cover"
            label={
              isFR
                ? "Image de couverture arrière"
                : "Back-cover artwork"
            }
            asset={backAsset}
            enabled={
              canEdit &&
              !coverApproved &&
              draft
                .backCover
                .approach ===
                  "existing_cover"
            }
            onUploaded={
              handleAssetUploaded
            }
          />
        </article>

        <article style={cardStyle}>
          <h3 className="ns-textbook-academic-title">
            3 · {isFR
              ? "Réglages d’impression"
              : "Print Settings"}
          </h3>

          <div
            className="ns-card-meta"
            style={{
              marginBottom: 12,
            }}
          >
            {isFR
              ? `Format : ${trim}`
              : `Book trim: ${trim}`}
          </div>

          <label>
            <strong>
              {isFR
                ? "Papier"
                : "Paper"}
            </strong>

            <select
              className="ns-input"
              value={
                draft
                  .printSettings
                  .paperType
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "printSettings",
                  "paperType",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir…"
                  : "Choose…"}
              </option>
              <option value="white">
                {isFR
                  ? "Blanc"
                  : "White"}
              </option>
              <option value="cream">
                {isFR
                  ? "Crème"
                  : "Cream"}
              </option>
            </select>
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Intérieur"
                : "Interior colour"}
            </strong>

            <select
              className="ns-input"
              value={
                draft
                  .printSettings
                  .interiorColor
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "printSettings",
                  "interiorColor",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir…"
                  : "Choose…"}
              </option>
              <option value="black_white">
                {isFR
                  ? "Noir et blanc"
                  : "Black & white"}
              </option>
              <option value="standard_color">
                {isFR
                  ? "Couleur standard"
                  : "Standard colour"}
              </option>
              <option value="premium_color">
                {isFR
                  ? "Couleur premium"
                  : "Premium colour"}
              </option>
            </select>
          </label>

          <label
            style={{
              display: "block",
              marginTop: 12,
            }}
          >
            <strong>
              {isFR
                ? "Reliure"
                : "Binding"}
            </strong>

            <select
              className="ns-input"
              value={
                draft
                  .printSettings
                  .binding
              }
              disabled={!canEdit}
              onChange={(event) =>
                update(
                  "printSettings",
                  "binding",
                  event.target.value
                )
              }
              style={{
                width: "100%",
                maxWidth: "none",
                marginTop: 6,
              }}
            >
              <option value="">
                {isFR
                  ? "Choisir…"
                  : "Choose…"}
              </option>
              <option value="paperback">
                {isFR
                  ? "Broché"
                  : "Paperback"}
              </option>
              <option value="hardcover">
                {isFR
                  ? "Relié"
                  : "Hardcover"}
              </option>
            </select>
          </label>
        </article>
      </div>

      {error ? (
        <div
          style={{
            marginTop: 14,
            color: "#b91c1c",
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      {message ? (
        <div
          style={{
            marginTop: 14,
            color: "#047857",
            fontWeight: 800,
          }}
        >
          {message}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 18,
          display: "flex",
          justifyContent:
            "flex-end",
        }}
      >
        <button
          type="button"
          className="ns-button"
          disabled={
            !canEdit ||
            saving
          }
          onClick={save}
        >
          {saving
            ? (isFR
                ? "Enregistrement…"
                : "Saving…")
            : (isFR
                ? "Enregistrer la préparation"
                : "Save Publication Preparation")}
        </button>
      </div>
    </section>
  );
}
